import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { translations } from '../i18n/translations';

const LanguageContext = createContext(null);

// Emotion caches for RTL and LTR
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const cacheLtr = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('fastline_lang');
    return saved === 'en' ? 'en' : 'ar';
  });

  const isRtl = language === 'ar';
  const dir = isRtl ? 'rtl' : 'ltr';

  useEffect(() => {
    localStorage.setItem('fastline_lang', language);
    document.documentElement.dir = dir;
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.lang = language;
    document.body.dir = dir;
    document.body.setAttribute('dir', dir);
    document.body.style.direction = dir;

    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.setAttribute('dir', dir);
      rootEl.style.direction = dir;
    }

    if (isRtl) {
      document.body.style.textAlign = 'right';
      document.body.style.fontFamily = "'Cairo', -apple-system, BlinkMacSystemFont, sans-serif";
    } else {
      document.body.style.textAlign = 'left';
      document.body.style.fontFamily = "'Plus Jakarta Sans', 'Segoe UI', Roboto, sans-serif";
    }
  }, [language, dir, isRtl]);

  const setLanguage = (newLang) => {
    if (newLang === 'ar' || newLang === 'en') {
      setLanguageState(newLang);
    }
  };

  const toggleLanguage = () => {
    setLanguageState((prev) => (prev === 'ar' ? 'en' : 'ar'));
  };

  // Helper translation function with nested key support e.g. t('nav.couriers')
  const t = useMemo(() => {
    return (keyPath, fallback = '') => {
      if (!keyPath) return '';
      const keys = keyPath.split('.');
      let current = translations[language];

      for (const k of keys) {
        if (current && typeof current === 'object' && k in current) {
          current = current[k];
        } else {
          // Fallback to Arabic if missing in current language
          let fallbackVal = translations.ar;
          for (const fk of keys) {
            if (fallbackVal && typeof fallbackVal === 'object' && fk in fallbackVal) {
              fallbackVal = fallbackVal[fk];
            } else {
              fallbackVal = null;
              break;
            }
          }
          return typeof fallbackVal === 'string' ? fallbackVal : (fallback || keyPath);
        }
      }

      return typeof current === 'string' ? current : (fallback || keyPath);
    };
  }, [language]);

  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    toggleLanguage,
    t,
    isRtl,
    dir,
  }), [language, isRtl, dir, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      <CacheProvider value={isRtl ? cacheRtl : cacheLtr}>
        {children}
      </CacheProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useTranslation = () => {
  const { t, language, isRtl, dir, setLanguage, toggleLanguage } = useLanguage();
  return { t, language, isRtl, dir, setLanguage, toggleLanguage };
};
